import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE MEMBER SURFACE'S OWN READ PATH (#185).
//
// The member app used to load the same way the portal does — twenty-odd
// `Storage.get` calls against `/kv`, which is CLUB-scoped and needs a club
// user's session. A player has neither, so the global `VITE_API_URL` switch
// would have answered 401 to every member.
//
// `/member/bootstrap` is the deliberate replacement: ONE request, scoped
// entirely by the member's own session. The club and the player come from the
// session, so there is no tenant to ask for and no member to name — which is
// also why nothing here takes an id.
// ─────────────────────────────────────────────────────────────────────

/** Everything a member screen needs, as the API projects it. */
export interface MemberBootstrap {
  me: Record<string, unknown> | null;
  members: Record<string, unknown>[];
  bookings: Record<string, unknown>[];
  activities: Record<string, unknown>[];
  plannedMatches: Record<string, unknown>[];
  singleMatches: Record<string, unknown>[];
  history: Record<string, unknown>[];
  club: Record<string, unknown> | null;
  bookingConfig: Record<string, unknown> | null;
  /**
   * Whether the club has SAVED its opening hours — no booking door opens before
   * that (owner decision 2026-10-31). Said by the server because the browser's
   * config state starts as the engine defaults, hours included. Absent on an
   * older server, and then true: what the screen did before.
   */
  bookingOpen: boolean;
  bookingZones: Record<string, unknown>[] | null;
  assurance: string | null;
  /** The venue's bookable equipment (#911 slice 2) — `MemberAsset`. */
  assets: Record<string, unknown>[];
  /** When each machine is taken — `MemberAssetBooking`. */
  assetBookings: Record<string, unknown>[];
}

/**
 * A machine a member may book beside a court, as `/member/bootstrap` projects
 * it (#911 slice 2): the price inputs the shared engine previews with, and no
 * owner — whose machine it is decides the centre ↔ club settlement, not anything
 * a member chooses or pays.
 */
export interface MemberAsset {
  id: string;
  name: string;
  kind: string;
  description: string | null;
  active: boolean;
  sortOrder: number;
  priceZones: unknown[] | null;
  defaultPricePerHour: number;
  bufferMin: number;
}

/**
 * One reservation of a machine. Another member's is a time and nothing else;
 * the member's own carries its court booking, price and payment status.
 */
export interface MemberAssetBooking {
  id: string;
  assetId: string;
  date: string;
  startTime: string;
  endTime: string;
  durationMin: number;
  mine: boolean;
  bookingId?: string | null;
  priceNok?: number;
  paymentStatus?: string;
}

/**
 * Is the app in MEMBER MODE?
 *
 * RE-EXPORTED, NOT DEFINED HERE (#750 M2b-2). The predicate moved to
 * `customerContext.ts`, which the TRANSPORT imports — and this module imports
 * the transport, so defining it here made `http → customerContext → member →
 * http` a cycle. The symptom was not a build error: the binding was in its
 * temporal dead zone when the transport first asked, and the member app simply
 * never called `/member/*` at all.
 *
 * The name and every call site are unchanged; only the definition moved, and a
 * second copy was never an option — see the guard in
 * `member-mode-after-a-reload.test.ts` on what one line of this predicate
 * decides.
 */
export { memberModeActive } from './customerContext';

const list = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : [];

/**
 * Fetch the member's view of their club.
 *
 * THROWS on a non-2xx. The caller has nothing to fall back to: reading the
 * club's data from `/kv` is exactly what a member may not do, so a failure has
 * to surface rather than quietly leave the app on an empty state that looks
 * like a club with no bookings.
 */
export async function fetchMemberBootstrap(): Promise<MemberBootstrap> {
  const res = await apiFetch('/member/bootstrap');
  if (!res.ok) {
    throw new Error(`[member] bootstrap failed: ${res.status}`);
  }
  const raw = (await res.json()) as Record<string, unknown>;
  // Normalised at the boundary so every consumer sees the same shapes whatever
  // the server omitted.
  return {
    me: (raw.me as Record<string, unknown>) ?? null,
    members: list(raw.members),
    bookings: list(raw.bookings),
    activities: list(raw.activities),
    plannedMatches: list(raw.plannedMatches),
    singleMatches: list(raw.singleMatches),
    history: list(raw.history),
    club: (raw.club as Record<string, unknown>) ?? null,
    bookingConfig: (raw.bookingConfig as Record<string, unknown>) ?? null,
    bookingOpen: raw.bookingOpen !== false,
    bookingZones: Array.isArray(raw.bookingZones)
      ? (raw.bookingZones as Record<string, unknown>[])
      : null,
    assurance: raw.assurance == null ? null : String(raw.assurance),
    // ABSENT ON AN OLDER SERVER, and then simply «no equipment here»: the
    // booking screen offers none, which is what the venue had before #911.
    assets: list(raw.assets),
    assetBookings: list(raw.assetBookings),
  };
}

// ─────────────────────────────────────────────────────────────────────
// MEMBER WRITES (#185).
//
// A member books through `POST /member/bookings`, not through `/bookings`. The
// difference is not the URL: the club command takes a calendar the client read
// earlier and an `adminOverride` flag, while this one derives everything from
// the member's own session and refuses what a member may not do — a slot the
// slot-finder would not have offered, one in the past, one outside the booking
// window. A member calling the club route is simply refused, so the branch is
// not an optimisation.
// ─────────────────────────────────────────────────────────────────────

/**
 * What a member sends to book a court. The member comes from the session.
 *
 * NO PRICE, AND NO PAYMENT STATUS. The server computes the price from the
 * club's own zones and decides the status — `pending` when there is something
 * to pay, `free` otherwise — because both used to come from the request, and a
 * member could name their own price or declare a booking paid.
 *
 * They are absent from this type rather than sent-and-ignored. The server
 * discards them either way, but a client that sends a price reads as one whose
 * price matters, and the day somebody loosens the server the client is already
 * primed to be trusted. The end time is the same: the slot generator's, not
 * the caller's.
 */
export interface MemberBookingIntent {
  court: number;
  date: string;
  startTime: string;
  endTime?: string;
  durationMin?: number;
  notes?: string;
  /**
   * Machines at this venue to book for the court's whole time (#911 slice 2).
   * Booked in the same command as the court, or not at all, and paid for in the
   * court's one payment. The PRICE is not sent: the server prices each machine
   * from its own zones, as it prices the court.
   */
  assetIds?: string[];
  /**
   * Idempotency key. Two taps, one booking.
   *
   * DERIVED from the slot rather than generated, so a retry of the SAME
   * intent carries the same key while a genuinely different booking gets a
   * different one. A random key per attempt would make the server`s
   * idempotency guarantee unreachable from here — which is the failure mode
   * worth designing out, because it only shows up as a double booking under a
   * flaky network.
   */
  requestId?: string;
}

/** The key a repeat of THIS booking would produce. Stable, and not a secret. */
export function memberBookingRequestId(intent: MemberBookingIntent): string {
  const base = [
    intent.court,
    intent.date,
    intent.startTime,
    intent.durationMin ?? intent.endTime ?? '',
  ]
    .map((p) => String(p ?? ''))
    .join(':');
  // THE EQUIPMENT IS PART OF THE INTENT (#911). The server refuses a reused key
  // whose machines differ, so the same court asked for again with another
  // machine must carry another key. Sorted, so the order a member ticked them
  // in is not a different request. No equipment keeps the key it always had.
  const assets = [...(intent.assetIds ?? [])].sort();
  return assets.length ? `${base}:${assets.join('+')}` : base;
}

/**
 * What the server refused, in Norwegian, for a player.
 *
 * The API answers a machine code; a member needs a sentence. Every code the
 * command can return is covered, and an unknown one falls back rather than
 * rendering the raw token — a screen showing `slot_in_the_past` to a player is
 * worse than one that says something vague.
 */
export function memberBookingRefusal(code: string, reason?: string | null): string {
  switch (code) {
    case 'slot_not_available':
      return 'Tiden er ikke ledig lenger. Velg en annen.';
    case 'slot_in_the_past':
      return 'Den tiden har vært.';
    case 'date_outside_booking_window':
      return 'Du kan ikke booke så langt fram ennå.';
    case 'duration_not_allowed':
      return 'Den lengden kan ikke bookes her.';
    case 'court_out_of_service':
      return 'Banen er ute av drift.';
    case 'member_not_found':
      return 'Du står ikke på klubbens liste lenger.';
    case 'request_id_reused':
      return 'Den bookingen er allerede registrert.';
    case 'booking_rejected':
      return reason || 'Bookingen ble avvist.';
    case 'missing_fields':
      return 'Noe mangler i bookingen.';
    case 'booking_hours_not_set':
      return 'Booking er ikke åpnet ennå – klubben har ikke lagt inn åpningstidene.';
    // ── NOT REFUSALS: THE REQUEST DID NOT GET AN ANSWER, OR THE SESSION IS GONE
    // (#1145, AUD-026). «Bookingen ble avvist» for either sent people looking for
    // a rule that did not exist. A retry is safe: the request id is derived from
    // the booking, so the server answers a repeat with the booking it made.
    case 'unreachable':
      return 'Fikk ikke kontakt med serveren. Prøv igjen — du blir ikke booket to ganger.';
    case 'unauthorized':
      return 'Du er logget ut. Logg inn på nytt for å booke.';
    default:
      return 'Bookingen ble avvist.';
  }
}

export interface MemberBookingResult {
  success: boolean;
  booking?: Record<string, unknown>;
  /** The machines booked with the court, as the server wrote them (#911). */
  assetBookings?: Record<string, unknown>[];
  error?: string;
  /**
   * The server's CODE when it refused (#911). The equipment refusals are
   * translated by the screen through the catalog, so the code travels beside
   * the legacy sentence rather than being turned into one here.
   */
  code?: string;
  /** Which machine a refusal is about, when it is about one. */
  assetId?: string;
}

/**
 * Book a court as the signed-in member.
 *
 * Never throws. A refusal is an ordinary answer, and so — since #1145 — is no
 * answer at all: `code: 'unreachable'`, a different thing from «the club said
 * no», and said differently. A 401 is `code: 'unauthorized'`.
 */
/** The request got no answer: say so, with the code a caller can test for. */
function unanswered(): MemberBookingResult {
  return { success: false, code: 'unreachable', error: memberBookingRefusal('unreachable') };
}

export async function createMemberBooking(
  intent: MemberBookingIntent,
): Promise<MemberBookingResult> {
  let res: Response;
  try {
    res = await apiFetch('/member/bookings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // BUILT FIELD BY FIELD, not spread.
      //
      // Spreading forwards whatever the caller happened to hold — and the caller
      // here is a shared booking handler that also serves the club path, where
      // `adminOverride` and `overrideCourtOutOfService` are ordinary arguments.
      // The type says they are not part of this intent; the type does not travel
      // at runtime. This is the only place that decides what a member sends.
      body: JSON.stringify({
        court: intent.court,
        date: intent.date,
        startTime: intent.startTime,
        endTime: intent.endTime,
        durationMin: intent.durationMin,
        notes: intent.notes,
        // Only when there is something to send: a request without equipment is
        // byte-for-byte what it was before #911.
        ...(intent.assetIds && intent.assetIds.length ? { assetIds: intent.assetIds } : {}),
        requestId: intent.requestId ?? memberBookingRequestId(intent),
      }),
    });
  } catch {
    // NO ANSWER — a thrown fetch or the transport's deadline. ANSWERED rather
    // than thrown (#1145): the booking screen awaited this with nothing to catch
    // it, so the button stayed on «Booker …» for good.
    return unanswered();
  }
  if (res.status === 502 || res.status === 503 || res.status === 504) return unanswered();
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok) {
    return {
      success: true,
      booking: (body?.booking as Record<string, unknown>) ?? undefined,
      assetBookings: list(body?.assetBookings),
    };
  }
  const code = res.status === 401 ? 'unauthorized' : String(body?.error ?? '');
  return {
    success: false,
    error: memberBookingRefusal(code, body?.reason == null ? null : String(body.reason)),
    code,
    ...(typeof body?.assetId === 'string' ? { assetId: body.assetId } : {}),
  };
}
